import { useCallback, useEffect, useState } from 'react';
import { getPolicy } from '../api/schoolConfigApi';
import type { components } from '../api/types.gen';
import { logError } from '../utils/errorHandling';

/** The PassPolicy payload returned by getPolicy (null when none is configured). */
export type PassPolicy = components['schemas']['PassPolicyResponse'];

/** Public shape returned by {@link usePassPolicy}. */
export interface UsePassPolicyResult {
  policy: PassPolicy | null;
  loading: boolean;
  error: Error | null;
  refetch: (maybeCancelled?: unknown) => Promise<void>;
}

/**
 * Fetches a school's PassPolicy — shared by PassPolicyPanel.js (the policy
 * editor) and StudentPassLimitsPanel.js (which only needs policy.interval
 * to gate its per-student limit input). A 404 means 'no policy configured
 * yet', not an error: policy resolves to null in that case, surfaced via
 * `error` only for non-404 failures so each caller can show its own
 * context-specific error copy.
 */
export function usePassPolicy(
  schoolId: number | string | null | undefined
): UsePassPolicyResult {
  const [policy, setPolicy] = useState<PassPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPolicy = useCallback(
    async (maybeCancelled?: unknown): Promise<void> => {
      // `refetch` is exposed publicly and is sometimes wired straight to an
      // event handler (e.g. PassPolicyPanel's Reset button, onClick={refetch}),
      // which calls this with a DOM event as the first argument. Only treat the
      // argument as a cancellation predicate when it's actually a function;
      // otherwise (event object, or no arg) fall back to "never cancelled".
      const isCancelled: () => boolean =
        typeof maybeCancelled === 'function'
          ? (maybeCancelled as () => boolean)
          : () => false;
      if (!schoolId) {
        // Synchronous, no await before this point — nothing can race it.
        setPolicy(null);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await getPolicy(schoolId);
        if (isCancelled()) return;
        setPolicy(result);
      } catch (err) {
        if (isCancelled()) return;
        if ((err as { status?: unknown }).status === 404) {
          setPolicy(null);
        } else {
          logError(err, 'client_api', 'usePassPolicy');
          setError(err as Error);
        }
      } finally {
        // A cancelled call must not flip loading for the newer schoolId.
        if (!isCancelled()) setLoading(false);
      }
    },
    [schoolId]
  );

  useEffect(() => {
    let cancelled = false;
    fetchPolicy(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchPolicy]);

  return { policy, loading, error, refetch: fetchPolicy };
}

export default usePassPolicy;
